import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLang } from '../../contexts/LangContext';
import useUnreadByCategory from '../../hooks/useUnreadByCategory';
import axios from 'axios';
import Wordmark from '../Wordmark';
import SpanishCharsGlobal from '../SpanishCharsGlobal';
import { avatarUrl } from '../../utils/fileUrl';
import {
  Books,
  Video,
  User,
  SignOut,
  ChalkboardTeacher,
  Robot,
  Handshake,
  Lightning,
  GraduationCap,
  UsersThree,
  FilmStrip,
  FileText,
  Trophy,
  ChartLineUp,
  Bell,
  Question,
  GameController,
  Textbox,
  BookOpen,
  PencilLine,
  MicrophoneStage,
  SpeakerHigh,
  Headphones
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=50`, { withCredentials: true });
      setItems(res.data.items || []);
      setUnread(res.data.unread_count || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  const markAll = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { withCredentials: true });
      setUnread(0);
      setItems(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    } catch { /* ignore */ }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => {
          const isOpening = !open;
          setOpen(v => !v);
          if (isOpening) {
            load();
            if (unread > 0) markAll();
          }
        }}
        className="relative w-9 h-9 rounded-full flex items-center justify-center text-[#57534E] hover:bg-stone-100 transition-colors"
        aria-label="Benachrichtigungen"
        data-testid="notifications-bell"
      >
        <Bell size={20} weight={unread > 0 ? 'fill' : 'regular'} className={unread > 0 ? 'text-[#AB1C24]' : ''} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#AB1C24] text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-unread">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="fixed left-3 right-3 top-16 lg:absolute lg:left-auto lg:right-0 lg:top-auto lg:mt-2 w-auto lg:w-80 max-w-none lg:max-w-[92vw] bg-white rounded-xl shadow-lg border border-stone-200 z-50 overflow-hidden"
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-stone-100">
            <span className="font-heading font-semibold text-[#292524]">Benachrichtigungen</span>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-[#AB1C24] hover:underline" data-testid="notifications-mark-all">Alle gelesen</button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-8 text-center text-sm text-[#78716C]">Keine Benachrichtigungen</div>
            ) : (
              items.map(n => {
                const ts = n.created_at ? new Date(n.created_at) : null;
                const tsLabel = ts ? ts.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
                return (
                <Link
                  key={n.notif_id}
                  to={n.link || '#'}
                  onClick={() => setOpen(false)}
                  className={`block px-4 py-3 border-b border-stone-100 hover:bg-stone-50 ${!n.read_at ? 'bg-amber-50/40' : ''}`}
                  data-testid={`notif-${n.notif_id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read_at && <span className="w-2 h-2 rounded-full bg-[#AB1C24] mt-1.5 flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-[#292524]">{n.title}</p>
                      <p className="text-xs text-[#57534E] mt-0.5">{n.body}</p>
                      {tsLabel && <p className="text-[10px] text-[#A8A29E] mt-1">{tsLabel}</p>}
                    </div>
                  </div>
                </Link>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const isTeacherRole = user?.role === 'admin' || user?.role === 'teacher';
  const { lang } = useLang();
  const isDe = lang === 'de';
  const location = useLocation();
  const isActive = (path) => location.pathname === path;
  // Active-check for a dropdown group. An item is considered inside this group
  // when either its path matches exactly, or (for deep-link items like
  // /materials?tile=speaking) pathname + search match.
  const groupContainsCurrent = (items) => items.some(i => {
    const [p, q] = i.path.split('?');
    if (location.pathname !== p) return false;
    if (!q) return !location.search;
    return location.search.includes(q);
  });
  const [reviewCount, setReviewCount] = useState(0);
  const [mobileMenu, setMobileMenu] = useState(null);
  const unread = useUnreadByCategory();

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'teacher') {
      axios.get(`${API}/review-queue/count`, { withCredentials: true })
        .then(res => setReviewCount(res.data.count || 0))
        .catch(() => {});
      const interval = setInterval(() => {
        axios.get(`${API}/review-queue/count`, { withCredentials: true })
          .then(res => setReviewCount(res.data.count || 0))
          .catch(() => {});
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.role]);

  const ubenItems = [
    { path: '/vocabulary', label: 'Vocabulario', icon: Textbox, active: isActive('/vocabulary'), badge: unread.vocabulary, badgeKey: 'vocabulary' },
    { path: '/materials?tile=speaking', label: isDe ? 'Sprechen' : 'Hablar', icon: MicrophoneStage, active: location.pathname === '/materials' && location.search.includes('speaking'), badge: unread.speaking, badgeKey: 'speaking' },
    { path: '/materials?tile=listening', label: isDe ? 'Hören' : 'Escuchar', icon: SpeakerHigh, active: location.pathname === '/materials' && location.search.includes('listening'), badge: unread.listening, badgeKey: 'listening' },
    { path: '/schreiben', label: isDe ? 'Schreiben' : 'Escribir', icon: PencilLine, active: isActive('/schreiben'), badge: unread.writing, badgeKey: 'writing' },
    { path: '/materials?tile=reading_exercises', label: isDe ? 'Lesen' : 'Leer', icon: BookOpen, active: location.pathname === '/materials' && location.search.includes('reading_exercises'), badge: unread.reading, badgeKey: 'reading' },
    { path: '/ai-teacher', label: 'Spiele', icon: GameController, active: isActive('/ai-teacher'), badge: unread.ai_teacher, badgeKey: 'ai_teacher' },
    { path: '/challenges', label: isDe ? 'Challenge' : 'Desafios', icon: Lightning, active: isActive('/challenges'), badge: unread.challenges, badgeKey: 'challenges' },
    { path: '/videos', label: 'Media', icon: FilmStrip, active: isActive('/videos'), badge: unread.videos, badgeKey: 'videos' },
  ];

  const kurseItems = [
    { path: '/materials', label: isDe ? 'Kurs' : 'Curso', icon: FileText, active: isActive('/materials') && !location.search, badge: unread.materials, badgeKey: 'materials' },
    { path: '/buddies', label: isDe ? 'Freunde' : 'Amigos', icon: UsersThree, active: isActive('/buddies'), badge: unread.buddies, badgeKey: 'buddies' },
    { path: '/dudas', label: isDe ? 'Fragen' : 'Dudas', icon: Question, active: isActive('/dudas'), badge: unread.dudas, badgeKey: 'dudas' },
    { path: '/lernanalyse', label: isDe ? 'Analyse' : 'Análisis', icon: ChartLineUp, active: isActive('/lernanalyse'), badge: unread.analysis, badgeKey: 'analysis' },
    { path: '/classes', label: isDe ? 'Videoanruf' : 'Videollamada', icon: Video, active: isActive('/classes'), badge: unread.classes, badgeKey: 'classes' },
  ];

  return (
    <div className="min-h-screen canvas-spain">
      {/* Floating Spanish-chars keyboard bar — auto-follows the focused
          textarea so students can insert á/é/í/ó/ú/ñ/ü/¿/¡/" comfortably on
          a German keyboard. Teachers don't need it, we skip rendering. */}
      <SpanishCharsGlobal enabled={!isTeacherRole} />
      {/* Desktop Header */}
      <header className="hidden lg:block fixed left-0 right-0 top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-1" data-testid="logo-link">
            <img src="/brand/hca-mark-512.png" alt="Habla" className="h-20 w-20 object-contain drop-shadow-sm" />
            <Wordmark size="lg" className="-ml-1" />
          </Link>

          {/* Navigation — direct links (no dropdowns) */}
          <nav className="flex items-center gap-1">
            <Link
              to="/uben"
              data-testid="nav-uben"
              className={`nav-link flex items-center gap-1.5 ${isActive('/uben') || groupContainsCurrent(ubenItems) ? 'active' : ''}`}
            >
              <GameController size={20} weight={isActive('/uben') ? 'fill' : 'regular'} />
              <span>{isDe ? 'Üben' : 'Practicar'}</span>
            </Link>

            <Link
              to="/kurs"
              data-testid="nav-kurs"
              className={`nav-link flex items-center gap-1.5 ${isActive('/kurs') || groupContainsCurrent(kurseItems) ? 'active' : ''}`}
            >
              <GraduationCap size={20} weight={isActive('/kurs') ? 'fill' : 'regular'} />
              <span>{isDe ? 'Kurs' : 'Curso'}</span>
            </Link>

            <Link
              to="/profile"
              data-testid="nav-profile"
              className={`nav-link flex items-center gap-1.5 ${isActive('/profile') ? 'active' : ''}`}
            >
              <User size={20} weight={isActive('/profile') ? 'fill' : 'regular'} />
              <span>{isDe ? 'Profil' : 'Perfil'}</span>
            </Link>

            {isTeacherRole && (
              <Link
                to="/teacher"
                data-testid="nav-teacher"
                className={`nav-link flex items-center gap-1.5 relative ${isActive('/teacher') ? 'active' : ''}`}
              >
                <ChalkboardTeacher size={20} weight={isActive('/teacher') ? 'fill' : 'regular'} />
                <span>{isDe ? 'Lehrer' : 'Profesor'}</span>
                {reviewCount > 0 && (
                  <span className="absolute -top-1 -right-2 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="review-badge">
                    {reviewCount}
                  </span>
                )}
              </Link>
            )}
          </nav>

          {/* User Info & Logout */}
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="flex items-center gap-2">
              {user?.avatar ? (
                <img src={avatarUrl(user.avatar)} alt={user.name} className="w-9 h-9 rounded-full object-cover border-2 border-white shadow-sm" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-[#AB1C24] flex items-center justify-center text-white font-bold">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-sm font-medium text-[#292524]">{user?.name}</span>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="p-2 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Cerrar sesión"
            >
              <SignOut size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="lg:hidden fixed left-0 right-0 top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-stone-200">
        <div className="px-3 py-1.5 flex items-center justify-between gap-2">
          <Link to="/dashboard" className="flex items-center gap-0.5 flex-shrink-0" data-testid="mobile-logo-link">
            <img src="/brand/hca-mark-256.png" alt="Habla" className="h-16 w-auto object-contain flex-shrink-0" />
            <Wordmark size="md" className="-ml-1" />
          </Link>
          <div className="flex items-center gap-1 flex-shrink-0">
            <NotificationBell />
            <Link to="/profile" className="flex-shrink-0" data-testid="mobile-profile-link">
              {user?.avatar ? (
                <img src={avatarUrl(user.avatar)} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#AB1C24] flex items-center justify-center text-white text-sm font-bold">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}
            </Link>
            <button
              onClick={logout}
              data-testid="mobile-logout-btn"
              className="p-2 rounded-lg text-stone-400 hover:text-red-600 active:bg-red-50 transition-colors"
              title="Cerrar sesión"
              aria-label="Cerrar sesión"
            >
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="pt-28 lg:pt-36 pb-24 lg:pb-10 px-4 lg:px-6">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </main>

      {/* Mobile submenu overlay */}
      {mobileMenu && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          onClick={() => setMobileMenu(null)}
          data-testid="mobile-submenu-backdrop"
        >
          <div
            className="absolute bottom-20 left-4 right-4 bg-white rounded-2xl border border-stone-200 shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4"
            onClick={e => e.stopPropagation()}
            data-testid="mobile-submenu"
          >
            <div className="px-4 py-3 border-b border-stone-100">
              <p className="font-semibold text-[#292524]">{mobileMenu.label}</p>
            </div>
            <div className="py-2">
              {mobileMenu.items.map(sub => (
                <Link
                  key={sub.path}
                  to={sub.path}
                  onClick={() => setMobileMenu(null)}
                  data-testid={`mobile-submenu-${sub.path.slice(1)}`}
                  className={`flex items-center gap-3 px-4 py-3.5 hover:bg-stone-50 transition-colors ${isActive(sub.path) ? 'text-[#AB1C24] bg-red-50/40' : 'text-[#292524]'}`}
                >
                  <sub.icon size={22} weight={isActive(sub.path) ? 'fill' : 'regular'} />
                  <span className="font-medium">{sub.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-nav" data-testid="mobile-nav">
        {[
          { key: 'practicar', label: isDe ? 'Üben' : 'Practicar', icon: GameController, path: '/uben',   groupItems: ubenItems },
          { key: 'curso',     label: isDe ? 'Kurs' : 'Curso',     icon: GraduationCap,  path: '/kurs',   groupItems: kurseItems },
          { key: 'perfil',    label: isDe ? 'Profil' : 'Perfil',  icon: User,           path: '/profile' },
        ].map((item) => {
          const anyActive = isActive(item.path) || (item.groupItems && groupContainsCurrent(item.groupItems));
          return (
            <Link
              key={item.key}
              to={item.path}
              data-testid={`mobile-nav-${item.key}`}
              className={`mobile-nav-item ${anyActive ? 'active' : ''}`}
            >
              <item.icon size={20} weight={anyActive ? 'fill' : 'regular'} />
              <span>{item.label}</span>
            </Link>
          );
        })}
        {(user?.role === 'admin' || user?.role === 'teacher') && (
          <Link
            to="/teacher"
            data-testid="mobile-nav-teacher"
            className={`mobile-nav-item relative ${isActive('/teacher') ? 'active' : ''}`}
          >
            <ChalkboardTeacher size={20} weight={isActive('/teacher') ? 'fill' : 'regular'} />
            <span>{isDe ? 'Lehrer' : 'Profesor'}</span>
            {reviewCount > 0 && (
              <span className="absolute top-0.5 right-1.5 min-w-[14px] h-[14px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                {reviewCount}
              </span>
            )}
          </Link>
        )}
      </nav>
    </div>
  );
};

export default MainLayout;
